import { useMemo, type ReactNode } from "react";
import {
  formatQuoteLineTotal,
  formatQuoteUnitPrice,
} from "@/lib/quoteCurrency";
import {
  formatDateOnly,
  ITEM_UNIT_ABBREVIATION,
  WEIGHT_UNIT_ABBREVIATION,
  type Address,
  type CustomFieldValues,
  type PublicQuote,
  type PublicQuoteItemLine,
  type PublicQuoteLine,
} from "@forgepoint/shared";
import { computeLineNumbers, getQuoteEffectiveDate } from "../utils";
import {
  PdfPageViewer,
  usePdfPageInfo,
  type QuoteSection,
} from "../components/PdfPageViewer";

interface PublicQuoteTemplateErpQuotationProps {
  quote: PublicQuote;
  isExpired: boolean;
}

const PAGE_PADDING_X = 20;
const PAGE_PADDING_Y = 18;
const ITEM_COLGROUP = (
  <colgroup>
    <col style={{ width: "5%" }} />
    <col style={{ width: "15%" }} />
    <col style={{ width: "7%" }} />
    <col style={{ width: "21%" }} />
    <col style={{ width: "13%" }} />
    <col style={{ width: "6%" }} />
    <col style={{ width: "16%" }} />
    <col style={{ width: "17%" }} />
  </colgroup>
);

/**
 * Epicor-style public quotation with location branding and compact item blocks.
 * @param quote - The hydrated quote data
 */
export function PublicQuoteTemplateErpQuotation({
  quote,
}: PublicQuoteTemplateErpQuotationProps) {
  const sections = useErpQuotationSections(quote);

  return (
    <div className="font-sans text-black">
      <PdfPageViewer
        sections={sections}
        repeatableHeader={<PageHeader quote={quote} />}
        repeatableFooter={<TotalsFooter quote={quote} />}
        pagePaddingX={PAGE_PADDING_X}
        pagePaddingY={PAGE_PADDING_Y}
        watermark={quote.watermark}
      />
    </div>
  );
}

function useErpQuotationSections(quote: PublicQuote): QuoteSection[] {
  const lines = useMemo(() => quote.lines ?? [], [quote.lines]);
  const lineNumbers = useMemo(() => computeLineNumbers(lines), [lines]);
  const sections: QuoteSection[] = [
    {
      key: "quote-information",
      content: <QuoteInformation quote={quote} />,
    },
  ];

  lines.forEach((line, index) => {
    const content = renderLine(line, lineNumbers[index], quote);
    if (content) {
      sections.push({ key: line.id || `line-${index}`, content });
    }
  });

  return sections;
}

function PageHeader({ quote }: { quote: PublicQuote }) {
  const { pageNumber, totalPages } = usePdfPageInfo();
  const logoUrl = quote.location?.logoUrl ?? null;
  const sellerName = getSellerName(quote);

  return (
    <div className="mb-1 grid h-[82px] grid-cols-[205px_1fr_205px] grid-rows-[41px_41px]">
      <div className="row-span-2 flex h-[78px] w-[205px] items-center pl-1">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={`${sellerName} logo`}
            className="max-h-[76px] max-w-[200px] object-contain object-left"
          />
        ) : (
          <p className="text-lg font-bold">{sellerName}</p>
        )}
      </div>
      <p className="col-start-2 row-start-2 justify-self-center text-[15px] font-bold">
        QUOTE
      </p>
      <p className="col-start-3 row-start-1 justify-self-end pr-1 pt-1 text-[10px]">
        Page: {pageNumber} of {totalPages}
      </p>
    </div>
  );
}

function QuoteInformation({ quote }: { quote: PublicQuote }) {
  const locationAddress =
    quote.location?.address ?? quote.organization?.address ?? null;
  const sellerName = getSellerName(quote);
  const sellerAddressLines = formatCompactAddress(locationAddress);
  const customerAddressLines = formatCompactAddress(
    quote.customerCompany?.address,
  );

  return (
    <div className="text-[11px] leading-[1.18]">
      <div className="mb-6 grid grid-cols-[1fr_260px] gap-8">
        <div>
          <p className="text-[14px] font-bold">{sellerName}</p>
          {sellerAddressLines.map((line) => (
            <p key={line}>{line}</p>
          ))}
          {quote.organization?.phone && (
            <p className="mt-1">Phone: {quote.organization.phone}</p>
          )}
        </div>
        <MetaRows
          rows={[
            ["Quote Number:", quote.quoteDisplayNumber],
            ["Date:", formatDate(getQuoteEffectiveDate(quote))],
            ["Expires:", formatDate(quote.expirationDate)],
          ]}
        />
      </div>

      <div className="mb-10 grid min-h-[115px] grid-cols-[1.42fr_0.82fr_1fr] gap-7">
        <div>
          <div className="grid grid-cols-[70px_1fr] gap-1">
            <p className="font-bold">Quote To:</p>
            <div>
              {quote.customerCompany?.name && (
                <p>{quote.customerCompany.name}</p>
              )}
              {customerAddressLines.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </div>
          <div className="mt-10 grid grid-cols-[70px_1fr] gap-1">
            <p className="font-bold">Phone:</p>
            <p>{quote.customerCompany?.phone}</p>
          </div>
        </div>

        <MetaRows
          rows={[
            ["Reference:", getCustomField(quote.customFields, "Reference")],
            ["Sales Person:", quote.salesRepName],
          ]}
        />

        <MetaRows
          rows={[
            [
              "Shipping Terms:",
              getCustomField(quote.customFields, "Shipping Terms") ??
                quote.location?.name,
            ],
            ["Incoterms:", getCustomField(quote.customFields, "Incoterms")],
            ["Ship Via:", getCustomField(quote.customFields, "Ship Via")],
            ["Terms:", quote.paymentTerms],
          ]}
        />
      </div>
    </div>
  );
}

function MetaRows({
  rows,
}: {
  rows: Array<[string, ReactNode | null | undefined]>;
}) {
  return (
    <div className="space-y-2">
      {rows.map(([label, value]) => (
        <div key={label} className="grid grid-cols-[105px_1fr] gap-1">
          <p className="text-right font-bold">{label}</p>
          <p>{value}</p>
        </div>
      ))}
    </div>
  );
}

function renderLine(
  line: PublicQuoteLine,
  lineNumber: number | null,
  quote: PublicQuote,
): ReactNode {
  if (line.type === "ITEM") {
    return <ItemBlock line={line} lineNumber={lineNumber} quote={quote} />;
  }

  if (line.type === "DISCOUNT") {
    if (!quote.showTotals) {
      return null;
    }
    const discount =
      line.amount !== null ? formatQuoteLineTotal(line, quote) : null;
    return (
      <SimpleLine
        description={line.description || "Discount"}
        amount={discount ? `-${discount}` : ""}
      />
    );
  }

  if (line.description) {
    return <SimpleLine description={line.description} />;
  }

  return null;
}

function ItemBlock({
  line,
  lineNumber,
  quote,
}: {
  line: PublicQuoteItemLine;
  lineNumber: number | null;
  quote: PublicQuote;
}) {
  const part = line.item?.name ?? "";
  const description =
    line.description && line.description !== part
      ? line.description
      : (line.item?.description ?? "");
  const drawing =
    getCustomField(line.customFields, "Drawing") ??
    getFileStem(line.item?.primaryFileAsset?.fileName);
  const analysis = getCustomField(line.customFields, "Analysis");
  const specification = getCustomField(line.customFields, "Specification");
  const netWeight = getNetWeight(line);

  return (
    <table className="mb-[18px] w-full table-fixed border-collapse text-[11px] leading-[1.2]">
      {ITEM_COLGROUP}
      <thead>
        <tr className="bg-[#dddddd] text-left font-bold">
          <th className="px-1 py-[5px]">Line</th>
          <th className="px-1 py-[5px]">Part</th>
          <th className="px-1 py-[5px]">Rev</th>
          <th className="px-1 py-[5px]">Drawing</th>
          <th className="px-1 py-[5px] text-right">Expected Qty</th>
          <th className="px-1 py-[5px]" />
          <th className="px-1 py-[5px] text-right">Unit Price</th>
          <th className="px-1 py-[5px] text-right">Ext. Price</th>
        </tr>
      </thead>
      <tbody>
        {line.quantityBreaks.map((quantityBreak, index) => (
          <tr key={`${line.id}-quantity-${index}`} className="align-top">
            <td className="px-1 pt-1">{index === 0 ? lineNumber : ""}</td>
            <td className="break-words px-1 pt-1 font-bold">
              {index === 0 ? part : ""}
            </td>
            <td className="px-1 pt-1">
              {index === 0 ? line.item?.revision : ""}
            </td>
            <td className="break-words px-1 pt-1">
              {index === 0 ? drawing : ""}
            </td>
            <td className="px-1 pt-1 text-right">
              {formatQuantity(quantityBreak.quantity)}
            </td>
            <td className="px-1 pt-1 uppercase">
              {line.unit ? ITEM_UNIT_ABBREVIATION[line.unit] : ""}
            </td>
            <td className="px-1 pt-1 text-right">
              {formatQuoteUnitPrice(quantityBreak, quote)}
            </td>
            <td className="px-1 pt-1 text-right">
              {formatQuoteLineTotal(quantityBreak, quote)}
            </td>
          </tr>
        ))}
        {(description || analysis || netWeight || specification) && (
          <tr>
            <td />
            <td colSpan={7} className="px-1 pt-2">
              {description && (
                <p className="mb-3 whitespace-pre-line">{description}</p>
              )}
              <DetailRow label="Analysis:" value={analysis} />
              <DetailRow label="Net Weight:" value={netWeight} />
              <DetailRow label="Specification:" value={specification} />
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function DetailRow({ label, value }: { label: string; value: string | null }) {
  if (!value) {
    return null;
  }
  return (
    <div className="mb-2 grid grid-cols-[115px_1fr]">
      <p>{label}</p>
      <p>{value}</p>
    </div>
  );
}

function SimpleLine({
  description,
  amount,
}: {
  description: string;
  amount?: string;
}) {
  return (
    <div className="mb-[18px] text-[11px]">
      <div className="flex justify-between bg-[#dddddd] px-1 py-[5px] font-bold">
        <p>{description}</p>
        <p>{amount}</p>
      </div>
    </div>
  );
}

function TotalsFooter({ quote }: { quote: PublicQuote }) {
  const { pageNumber, totalPages } = usePdfPageInfo();
  const showTotals = quote.showTotals && pageNumber === totalPages;

  return (
    <div className="flex h-[112px] items-end text-[11px]">
      {showTotals && (
        <div className="w-full border border-gray-500">
          <div className="grid grid-cols-[1fr_170px] py-2">
            <div className="pr-1 text-right leading-[1.45]">
              <p>Lines Total</p>
            </div>
            <div className="border-l border-gray-500 pr-1 text-right leading-[1.45]">
              <p>{formatAmount(quote.subtotal)}</p>
            </div>
          </div>
          <div className="grid grid-cols-[1fr_170px] border-t border-gray-500 bg-[#f1f1f1] py-[6px] text-[14px] font-bold">
            <p className="pr-1 text-right">Quote Total</p>
            <p className="border-l border-gray-500 pr-1 text-right">
              {getCurrencyCode(quote)} {formatAmount(getQuoteTotal(quote))}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function getCustomField(
  fields: CustomFieldValues | null | undefined,
  key: string,
): string | null {
  if (!fields) {
    return null;
  }
  const matchingKey = Object.keys(fields).find(
    (fieldName) => fieldName.toLowerCase() === key.toLowerCase(),
  );
  const value = matchingKey ? fields[matchingKey] : null;
  if (value === null || value === undefined) {
    return null;
  }
  const text = Array.isArray(value) ? value.join(", ") : String(value);
  return text.trim() || null;
}

function getSellerName(quote: PublicQuote): string {
  return (
    quote.location?.address?.name ||
    quote.location?.name ||
    quote.organization?.address?.name ||
    quote.organization?.name ||
    ""
  );
}

function formatCompactAddress(address: Address | null | undefined): string[] {
  if (!address) {
    return [];
  }
  return [
    address.address_line_1,
    address.address_line_2,
    [address.city, address.state, address.zipcode].filter(Boolean).join(" "),
    address.country,
  ].filter((value): value is string => Boolean(value));
}

function formatDate(value: Date | string | null | undefined): string {
  if (!value) {
    return "";
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return (
      formatDateOnly(
        value,
        { month: "numeric", day: "numeric", year: "numeric" },
        "en-US",
      ) ?? ""
    );
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}

function formatQuantity(value: string): string {
  const quantity = Number(value);
  return Number.isFinite(quantity)
    ? quantity.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : value;
}

function formatAmount(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return "";
  }
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : String(value);
}

function getQuoteTotal(quote: PublicQuote): string | null {
  return quote.total ?? quote.subtotalWithDiscounts ?? quote.subtotal;
}

function getCurrencyCode(quote: PublicQuote): string {
  return quote.quoteCurrency ?? quote.baseCurrency ?? "USD";
}

function getFileStem(fileName: string | null | undefined): string | null {
  return fileName?.replace(/\.[^.]+$/, "") || null;
}

function getNetWeight(line: PublicQuoteItemLine): string | null {
  const weight = line.item?.weight;
  if (!weight) {
    return null;
  }
  const parsedWeight = Number(weight);
  const formattedWeight = Number.isFinite(parsedWeight)
    ? parsedWeight.toLocaleString("en-US", {
        minimumFractionDigits: 5,
        maximumFractionDigits: 5,
      })
    : weight;
  const unit = line.item?.weightUnit
    ? WEIGHT_UNIT_ABBREVIATION[line.item.weightUnit].toUpperCase()
    : "";
  return [formattedWeight, unit].filter(Boolean).join(" ");
}
